import React, { useEffect, useState } from 'react';
import { Flex, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { User, Phone, ChevronDown, Check, Info, Settings, Zap } from 'lucide-react';
import { TableAction } from '@/components';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@/components/ui/popover';
import { Link } from 'react-router-dom';
import { normalizeToTranslationKey } from '@/utils';
import { ROUTE } from '@/config';
import { useResendStore } from '@/stores/useResendStore';
import { InventoryOrder, ResendPayload } from '@/types/inventory';
import { useGetAvailableCarriers } from '@/hooksApi/useDispatcher';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import { useEvaluateShippingRules, useListShippingRules } from '@/hooksApi/useShippingRule';
import { decideOrderRouting } from '@/utils/decideOrderRouting';
import { resolveRuleCarrier } from './resendRuleResolution';

// RS Ph3 bugfix — extend the row carrier shape with `carrier_uid` so we
// can match the rule's `assignedCarrierUid` against the freshly-fetched
// list. The legacy `available_carriers_detail` from the inventory API
// does not include the uid; only the dispatcher-preview adapter does.
type AvailableCarrier = InventoryOrder['available_carriers_detail'][number] & {
  carrier_uid?: string | null;
};

// Maps an adapted CarrierPreviewItem back to the AvailableCarrier shape used by the table
const adaptPreviewItemToCarrier = (
  item: {
    carrier: {
      uid?: string;
      slug: string;
      title: string;
      avatar: { abs_url: string } | null;
    };
    shipping_fee: string;
    serviceType?: string;
  },
): AvailableCarrier => ({
  carrier_id: 0,
  carrier_uid: item.carrier?.uid ?? null,
  carrier_slug: item.carrier?.slug ?? '',
  carrier_name: item.carrier?.title ?? '',
  logo_url: item.carrier?.avatar ? { abs_url: item.carrier.avatar.abs_url } : null,
  shipping_fee: parseFloat(item.shipping_fee) || 0,
  delivery_day: item.serviceType === 'same_day' ? 'same' : 'next',
});

interface ResendCarrierRow {
  key: string;
  uid: string;
  platform_code: string;
  full_name: string;
  phone: string;
  available_carriers: AvailableCarrier[];
  recommended_carrier_slug: string | null;
}

const getCarrierInitials = (name: string | undefined | null): string => {
  if (!name) return '?';
  const words = name.split(' ');
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const carrierColors: Record<string, string> = {
  'Al Saari': 'hsl(210,60%,50%)',
  'Hi Express': 'hsl(25,80%,50%)',
  Sandoog: 'hsl(145,50%,40%)',
  Waseet: 'hsl(260,50%,55%)',
  Zaeem: 'hsl(0,65%,50%)',
};

const getCarrierColor = (name: string): string => carrierColors[name] || 'hsl(210,10%,50%)';

interface CarrierLogoProps {
  logoUrl: string;
  title: string;
}

const CarrierLogo: React.FC<CarrierLogoProps> = ({ logoUrl, title }) => {
  const [failed, setFailed] = React.useState(false);
  if (!logoUrl || failed) {
    return (
      <div className='flex items-center gap-1.5'>
        <div
          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold'
          style={{ backgroundColor: getCarrierColor(title) }}
        >
          {getCarrierInitials(title)}
        </div>
        <span className='text-xs font-medium text-sd-foreground whitespace-nowrap'>{title}</span>
      </div>
    );
  }
  return (
    <div className='flex items-center gap-1.5'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white overflow-hidden shadow-sm border border-gray-100'>
        <img
          src={logoUrl}
          alt={title}
          width={28}
          height={28}
          className='object-contain'
          onError={() => setFailed(true)}
        />
      </div>
      <span className='text-xs font-medium text-sd-foreground whitespace-nowrap'>{title}</span>
    </div>
  );
};

const ResendStep2: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const {
    selectedOrders,
    orderOverrides,
    carrierSelections,
    freshCarriers,
    ruleDispatchByOrder,
    setCarrierForOrder,
    setOrderOverride,
    setFreshCarriers,
    setRuleDispatchForOrder,
  } = useResendStore();
  const { data: pickupAddressesData } = useFetchPickupAddresses();
  const { mutate: fetchAvailableCarriers } = useGetAvailableCarriers();
  const { mutate: evaluateRules } = useEvaluateShippingRules();
  // RS Ph3 — load rule list to render rule names in the per-row banner.
  const hasAnyRule = Object.values(ruleDispatchByOrder).some((d) => d?.winningRuleUid);
  const { data: rulesListResp } = useListShippingRules(hasAnyRule);


  // Resolve source region: use the merchant's default pickup address
  const defaultPickupAddress = pickupAddressesData?.object?.items?.find((a) => a.default) ?? pickupAddressesData?.object?.items?.[0];
  const sourceRegionUid = defaultPickupAddress?.region?.uid;
  const sourceProvinceCode = defaultPickupAddress?.region?.province_code ?? '';

  const [loadingCarriers, setLoadingCarriers] = useState(false);

  // Stable key derived from the destination region of each order — changes when the user edits
  // a customer location in Step 1, triggering a re-fetch for affected orders.
  const overridesKey = JSON.stringify(
    selectedOrders.map((o) => {
      const override = orderOverrides[o.uid]?.customer_data;
      // Use override value if customer_data exists (even if region_uid is undefined/cleared)
      return override && 'region_uid' in override ? override.region_uid : o.region?.uid;
    }),
  );

  useEffect(() => {
    if (!sourceRegionUid) return;

    let pendingCount = 0;
    setLoadingCarriers(true);

    selectedOrders.forEach((order) => {
      const override = orderOverrides[order.uid];
      // Use overridden region_uid if the customer was edited in Step 1
      const destinationRegionUid = override?.customer_data?.region_uid ?? order.region?.uid;
      if (!destinationRegionUid) return;

      pendingCount++;
      const productsValue = (order.products ?? []).reduce((sum, p) => {
        return sum + (parseFloat(String(p.price)) || 0) * (Number(p.quantity) || 1);
      }, 0);

      // RS Ph3 — fire rule eval in parallel with carrier fetch. Failures
      // are non-blocking; the resend flow falls through to manual.
      const destProvinceCode =
        override?.customer_data?.province_code ?? order.region?.province_code ?? '';
      evaluateRules(
        {
          source: sourceProvinceCode || undefined,
          destination: destProvinceCode || undefined,
          package_size: (order.size ? order.size.toUpperCase() : 'S') as
            | 'S'
            | 'M'
            | 'L'
            | 'XL',
          order_type: (order.type as 'DEFAULT' | 'EXCHANGE' | 'RETURN') ?? 'DEFAULT',
          fragile: !!order.is_fragile,
          qlue: false,
          // Resend's child order is COD by default; cod_amount drives any
          // amount-based rules (mirror of the new-order builder).
          cod_amount: order.fee
            ? Number(order.fee)
            : productsValue,
        } as Parameters<typeof evaluateRules>[0],
        {
          onSuccess: (response: any) => {
            try {
              const decision = decideOrderRouting(response);
              setRuleDispatchForOrder(
                order.uid,
                decision.action !== 'manual' && decision.winningRuleUid
                  ? {
                      action: decision.action,
                      winningRuleUid: decision.winningRuleUid,
                      // Bug 1 fix — preserve the rule's chosen carrier uid
                      // so we can resolve it against the freshly-fetched
                      // carrier list once that arrives. Only set for
                      // auto-dispatched (other actions don't pin a carrier).
                      assignedCarrierUid:
                        decision.action === 'auto-dispatched'
                          ? decision.assignedCarrierUid
                          : null,
                      assignedCarrierSlug: null, // resolved by effect below from carrier list
                      restrictedCarrierUids:
                        decision.action === 'restricted' ||
                        decision.action === 'auto-cheapest'
                          ? decision.restrictedCarrierUids
                          : undefined,
                    }
                  : null,
              );
            } catch {
              setRuleDispatchForOrder(order.uid, null);
            }
          },
          onError: () => setRuleDispatchForOrder(order.uid, null),
        } as any,
      );

      fetchAvailableCarriers(
        {
          source_region_uid: sourceRegionUid,
          destination_region_uid: destinationRegionUid,
          type: order.type || 'DEFAULT',
          size: order.size || 'S',
          is_fragile: order.is_fragile ?? false,
          products_value: productsValue,
        },
        {
          onSuccess: (data) => {
            const allCarriers = [
              ...(data.object.same_day_carriers ?? []),
              ...(data.object.next_day_carriers ?? []),
            ].map(adaptPreviewItemToCarrier);
            setFreshCarriers(order.uid, allCarriers);
            // Auto-select carrier if not already selected.
            // Bug 1 fix — when a rule auto-dispatched, the rule's carrier
            // takes precedence over `recommended_carrier_slug` (which is a
            // different system's recommendation). Resolve by uid first.
            const currentSelections = useResendStore.getState().carrierSelections;
            if (!currentSelections[order.uid] && allCarriers.length > 0) {
              const dispatchNow =
                useResendStore.getState().ruleDispatchByOrder[order.uid];
              let autoSlug: string | null = null;
              if (
                dispatchNow?.action === 'auto-dispatched' &&
                dispatchNow.assignedCarrierUid
              ) {
                const ruleCarrier = resolveRuleCarrier(
                  allCarriers,
                  dispatchNow.assignedCarrierUid,
                );
                if (ruleCarrier) autoSlug = ruleCarrier.carrier_slug;
              }
              if (!autoSlug) {
                const recSlug = order.recommended_carrier_slug;
                autoSlug =
                  recSlug && allCarriers.some((c) => c.carrier_slug === recSlug)
                    ? recSlug
                    : allCarriers[0].carrier_slug;
              }
              setCarrierForOrder(order.uid, autoSlug);
              setOrderOverride(order.uid, {
                ...(useResendStore.getState().orderOverrides[order.uid] ?? {}),
                carrier_slug: autoSlug,
              });
            }
            pendingCount--;
            if (pendingCount <= 0) setLoadingCarriers(false);
          },
          onError: () => {
            pendingCount--;
            if (pendingCount <= 0) setLoadingCarriers(false);
          },
        },
      );
    });

    if (pendingCount === 0) setLoadingCarriers(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overridesKey, sourceRegionUid]);

  const tableData: ResendCarrierRow[] = selectedOrders.map((order) => {
    const override = orderOverrides[order.uid];
    return {
      key: order.uid,
      uid: order.uid,
      platform_code: order.platform_code,
      full_name: override?.customer_data?.full_name ?? order.full_name,
      phone: override?.customer_data?.phone ?? order.phone,
      // Use freshly fetched carriers when available; fall back to stale inventory data
      available_carriers: freshCarriers[order.uid] ?? order.available_carriers_detail ?? [],
      recommended_carrier_slug: order.recommended_carrier_slug,
    };
  });

  const commitCarrierSelect = (orderUid: string, carrierSlug: string) => {
    setCarrierForOrder(orderUid, carrierSlug);
    const existing: ResendPayload = orderOverrides[orderUid] ?? {};
    setOrderOverride(orderUid, { ...existing, carrier_slug: carrierSlug });
    message.success(String(t('carrier_updated')));
  };

  const handleCarrierSelect = (orderUid: string, carrierSlug: string) => {
    // SRE-2: shipping rules are binding — the per-row carrier list is
    // already restricted to the rule's carriers, so any selectable carrier
    // is a valid in-rule choice. There is no override path.
    commitCarrierSelect(orderUid, carrierSlug);
  };

  // RS Ph3 — once both carriers + dispatch land, populate the rule's
  // assignedCarrierSlug on the dispatch record so override detection works.
  //
  // Bug 1 fix — resolve the slug by matching the rule's
  // `assignedCarrierUid` against the freshly-fetched carrier list. The
  // previous implementation took `carriers[0]` blindly which silently
  // mislabelled the rule's choice when the dispatcher returned an
  // unfiltered list.
  useEffect(() => {
    selectedOrders.forEach((order) => {
      const dispatch = ruleDispatchByOrder[order.uid];
      if (!dispatch || dispatch.action !== 'auto-dispatched') return;
      if (dispatch.assignedCarrierSlug) return;
      const carriers = freshCarriers[order.uid];
      if (!carriers || carriers.length === 0) return;
      const ruleCarrier = resolveRuleCarrier(
        carriers,
        dispatch.assignedCarrierUid,
      );
      if (!ruleCarrier) return; // can't resolve; leave as-is and don't lie
      setRuleDispatchForOrder(order.uid, {
        ...dispatch,
        assignedCarrierSlug: ruleCarrier.carrier_slug,
      });
      // Bug 1 fix — once we know the rule's carrier, ensure it's the
      // selected one (overrides any prior recommended_carrier_slug pick
      // made before the dispatch decision arrived).
      const currentSelections = useResendStore.getState().carrierSelections;
      if (currentSelections[order.uid] !== ruleCarrier.carrier_slug) {
        setCarrierForOrder(order.uid, ruleCarrier.carrier_slug);
        setOrderOverride(order.uid, {
          ...(useResendStore.getState().orderOverrides[order.uid] ?? {}),
          carrier_slug: ruleCarrier.carrier_slug,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ruleDispatchByOrder), JSON.stringify(freshCarriers)]);

  const columns: ColumnsType<ResendCarrierRow> = [
    {
      title: String(t('original_order')),
      dataIndex: 'platform_code',
      key: 'platform_code',
      render: (text: string) => (
        <span className='text-sm font-medium text-sd-foreground'>{text || '—'}</span>
      ),
    },
    {
      title: String(t('customer')),
      key: 'customer',
      render: (_: unknown, record: ResendCarrierRow) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.full_name}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-[#737b8c] shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-[#737b8c]'>{record.phone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: String(t('carrier')),
      key: 'carrier',
      render: (_: unknown, record: ResendCarrierRow) => {
        const activeSlug =
          carrierSelections[record.uid] ?? record.recommended_carrier_slug ?? null;
        const activeCarrier = record.available_carriers.find(
          (c) => c.carrier_slug === activeSlug,
        );

        if (record.available_carriers.length === 0) {
          return (
            <div className='sd-scope sd-font'>
              <Popover>
                <PopoverTrigger asChild>
                  <button className='flex items-center justify-center rounded-full w-7 h-7 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer p-0'>
                    <Info className='h-4 w-4 text-amber-500' strokeWidth={2} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className='sd-scope sd-font w-[220px] !p-0 rounded-xl !shadow-lg !border-gray-200 !bg-white'
                  align={isRtl ? 'end' : 'start'}
                >
                  <div className='bg-amber-50 px-3 py-3 rounded-xl'>
                    <div className='mb-2'>
                      <span className='text-xs font-medium text-amber-800'>
                        {String(t('no_active_carrier_available'))}
                      </span>
                    </div>
                    <Link
                      to={ROUTE.SETTINGS_SHIPPING_RULES}
                      className='inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-900 transition-colors no-underline'
                    >
                      <Settings className='h-3 w-3' strokeWidth={1.5} />
                      {String(t('carrier_settings'))}
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          );
        }

        const selectorContent = (
          <div className='py-1'>
            {record.available_carriers.map((c) => {
              const isSelected = c.carrier_slug === activeSlug;
              return (
                <PopoverClose asChild key={c.carrier_slug}>
                  <button
                    onClick={() => handleCarrierSelect(record.uid, c.carrier_slug)}
                    className='w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer text-left'
                  >
                    <div className='flex-1 flex items-center gap-2'>
                      <CarrierLogo
                        logoUrl={c.logo_url?.abs_url || ''}
                        title={String(t(normalizeToTranslationKey(c.carrier_name)))}
                      />
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      <span className='text-[11px] text-muted-foreground whitespace-nowrap'>
                        {c.shipping_fee != null && isFinite(c.shipping_fee)
                          ? Math.round(c.shipping_fee).toLocaleString()
                          : '—'}{' '}
                        {String(t('iqd'))}
                      </span>
                      {isSelected && (
                        <Check
                          className='h-3.5 w-3.5 text-emerald-600 shrink-0'
                          strokeWidth={2.5}
                        />
                      )}
                    </div>
                  </button>
                </PopoverClose>
              );
            })}
          </div>
        );

        return (
          <div className='sd-scope sd-font'>
            <Popover>
              <PopoverTrigger asChild>
                <button className='flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer group'>
                  {activeCarrier ? (
                    <CarrierLogo
                      logoUrl={activeCarrier.logo_url?.abs_url || ''}
                      title={String(t(normalizeToTranslationKey(activeCarrier.carrier_name)))}
                    />
                  ) : (
                    <span className='text-xs text-muted-foreground'>
                      {String(t('select_carrier'))}
                    </span>
                  )}
                  <ChevronDown
                    className='h-3 w-3 text-muted-foreground shrink-0 opacity-60 group-hover:opacity-100 transition-opacity'
                    strokeWidth={2}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='sd-scope sd-font w-[280px] !p-0 rounded-xl !shadow-lg !border-gray-200 !bg-white'
                align={isRtl ? 'end' : 'start'}
              >
                {selectorContent}
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
  ];

  // RS Ph3 — collect distinct rule names that fired across the selected
  // orders so we can render a single, compact banner above the table
  // explaining auto-dispatch attribution.
  const dispatchSummaries = selectedOrders
    .map((o) => {
      const d = ruleDispatchByOrder[o.uid];
      if (!d || !d.winningRuleUid) return null;
      const ruleName =
        rulesListResp?.object?.find((r) => r.uid === d.winningRuleUid)?.name ?? null;
      return { uid: o.uid, action: d.action, ruleName };
    })
    .filter(Boolean) as Array<{ uid: string; action: string; ruleName: string | null }>;

  return (
    <div className='border border-solid border-sd-border rounded-2xl bg-white p-6'>
      <h2 className='text-base font-semibold text-sd-foreground mt-0 mb-1'>
        {String(t('resend_step2_title'))}
      </h2>
      <p className='text-sm text-sd-muted-foreground mt-0 mb-4'>
        {String(t('resend_step2_subtitle'))}
      </p>

      {/* RS Ph3 — auto-dispatch transparency banner. Surfaces when at least
          one order in the resend batch had a shipping rule fire. */}
      {dispatchSummaries.length > 0 && (
        <div className='mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3'>
          <Zap className='h-5 w-5 shrink-0 text-blue-600 mt-0.5' strokeWidth={2} />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-blue-900 m-0'>
              {String(
                t('resend_rule_dispatch_banner', {
                  count: dispatchSummaries.length,
                  defaultValue:
                    '{{count}} order(s) auto-routed by your shipping rules.',
                }),
              )}
            </p>
          </div>
        </div>
      )}

      <TableAction<ResendCarrierRow>
        columns={columns}
        data={tableData}
        pageSize={tableData.length || 10}
        hidePagination
        customClassName='import-table'
        loading={loadingCarriers}
      />
    </div>
  );
};

export default ResendStep2;
